import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { getProfileAssetUrl } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Plus, Search, Edit, Trash2, Users, Mail, Phone, Briefcase } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const Teachers = () => {
  const { user } = useAuth();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', employee_id: '',
    designation: '', department: '', qualification: '', experience_years: '',
    date_of_birth: '', gender: '', joining_date: '', salary: '',
  });

  useEffect(() => { fetchTeachers(); }, [user?.school_id]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => fetchTeachers();
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [user?.school_id]);

  const fetchTeachers = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const schoolId = user?.school_id;
      const response = await api.get('/teachers', {
        params: {
          ...(schoolId ? { school_id: schoolId } : {}),
          ...(academicYearId ? { academic_year_id: academicYearId } : {}),
        },
      });
      setTeachers(response.data.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch teachers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTeacher) {
        await api.put(`/teachers/${editingTeacher.id}`, formData);
        toast({ title: 'Success', description: 'Teacher updated successfully' });
      } else {
        await api.post('/teachers', formData);
        toast({ title: 'Success', description: 'Teacher created. Credentials sent via email.' });
      }
      setIsDialogOpen(false);
      resetForm();
      fetchTeachers();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed', variant: 'destructive' });
    }
  };

  const handleEdit = (teacher) => {
    navigate(`/teachers/${teacher.id}/edit`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this teacher?')) {
      try {
        await api.delete(`/teachers/${id}`);
        toast({ title: 'Success', description: 'Teacher deleted' });
        fetchTeachers();
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
      }
    }
  };

  const resetForm = () => {
    setFormData({ first_name: '', last_name: '', email: '', phone: '', employee_id: '',
      designation: '', department: '', qualification: '', experience_years: '',
      date_of_birth: '', gender: '', joining_date: '', salary: '' });
    setEditingTeacher(null);
  };

  const filtered = teachers.filter(t => 
    `${t.first_name} ${t.last_name} ${t.employee_id} ${t.email}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="teachers-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Teachers</h1>
          <p className="text-slate-400">Manage teaching staff</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-teacher-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Teacher
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Teacher</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>First Name *</Label><Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} className="bg-slate-700 border-slate-600" required /></div>
                <div><Label>Last Name</Label><Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Email *</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="bg-slate-700 border-slate-600" required /></div>
                <div><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Employee ID *</Label><Input value={formData.employee_id} onChange={(e) => setFormData({...formData, employee_id: e.target.value})} className="bg-slate-700 border-slate-600" required /></div>
                <div><Label>Designation</Label><Input value={formData.designation} onChange={(e) => setFormData({...formData, designation: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Department</Label><Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                <div><Label>Qualification</Label><Input value={formData.qualification} onChange={(e) => setFormData({...formData, qualification: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Gender</Label>
                  <Select value={formData.gender} onValueChange={(v) => setFormData({...formData, gender: v})}>
                    <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Joining Date</Label><Input type="date" value={formData.joining_date} onChange={(e) => setFormData({...formData, joining_date: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">Create</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
            <Input placeholder="Search teachers..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-slate-700 border-slate-600 text-white" />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div>
      ) : filtered.length === 0 ? (
        <Card className="bg-slate-800 border-slate-700"><CardContent className="py-12 text-center">
          <Users className="w-12 h-12 text-slate-500 mx-auto mb-4" /><p className="text-slate-400">No teachers found</p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {filtered.map((teacher) => (
            <Card key={teacher.id} className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Link
                    to={`/teachers/${teacher.id}`}
                    className="flex items-center gap-4 flex-1 min-w-0"
                  >
                    <Avatar className="w-12 h-12 shrink-0">
                      <AvatarImage
                        src={getProfileAssetUrl(teacher.avatar)}
                        alt={`${teacher.first_name} ${teacher.last_name}`}
                        className="object-cover"
                      />
                      <AvatarFallback className="bg-green-500/20 text-green-400 font-semibold">
                        {teacher.first_name?.[0]?.toUpperCase()}{teacher.last_name?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-white hover:text-green-300 transition-colors">{teacher.first_name} {teacher.last_name}</h3>
                      <p className="text-sm text-slate-400">{teacher.employee_id} • {teacher.designation || 'Teacher'}</p>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-xs text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" /> {teacher.email}</span>
                        {teacher.department && <span className="text-xs text-slate-500 flex items-center gap-1"><Briefcase className="w-3 h-3" /> {teacher.department}</span>}
                      </div>
                    </div>
                  </Link>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="ghost" size="icon" onClick={(e) => { e.preventDefault(); handleEdit(teacher); }} className="text-slate-400 hover:text-white"><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={(e) => { e.preventDefault(); handleDelete(teacher.id); }} className="text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Teachers;
